import { getEnv } from '@luna/env';
import { request } from '@luna/api';
import { base64UrlEncode, uuid } from '@luna/utils';
import { setCookie } from '@luna/cookies';
import { logger } from '@luna/logger';
import { getCurrentLanguage } from '@luna/i18n';
import { getFeatureConfig } from '@luna/feature-config';
import type { RetailEnvironmentConfig } from '@retail/core-config';

import { isCsrfFeatureEnabled } from './startup/georgeStartService';

export const EN_LANGUAGE = 'en';
export const ACCESS_TOKEN_COOKIE = 'gt';
export const OVERVIEW_ROUTE = 'overview';
export const CSRF_TOKEN_COOKIE = 'gt_csrf';
export const ACCESS_TOKEN_PARAM = 'access_token';
export const STATE_PARAM = 'state';
export const TARGET_CLIENT_ID_PARAM = 'targetClientId';
export const TOKEN_EXCHANGE_ENDPOINT_PATH = 'exchange';

function hardReload(url: URL): void {
    window.history.pushState({}, '', url);
    window.location.reload();
}

function buildSelfExchangeUrl(requestParams: { [key: string]: string }) {
    const { clientId, stsUrl } = getEnv<RetailEnvironmentConfig>();

    const url = new URL(`${stsUrl}${TOKEN_EXCHANGE_ENDPOINT_PATH}`);
    url.searchParams.set(TARGET_CLIENT_ID_PARAM, clientId);
    Object.entries(requestParams).forEach(([key, value]) => {
        url.searchParams.set(key, value);
    });

    return url.toString();
}

export function getUrlToReloadTo(accessToken: string): URL {
    const url = new URL(window.location.href);
    url.hash = `${ACCESS_TOKEN_PARAM}=${accessToken}`;

    if (isCsrfFeatureEnabled()) {
        const csrfToken = uuid();
        setCookie(CSRF_TOKEN_COOKIE, csrfToken);
        url.hash += `&${STATE_PARAM}=||${csrfToken}`;
    }

    return url;
}

export async function triggerSelfHandover(requestParams: Record<string, string> = {}) {
    const exchangeUrl = buildSelfExchangeUrl(requestParams);

    try {
        const { access_token: accessToken } = await request<{ access_token: string }>(exchangeUrl, {
            credentials: 'include',
        });

        const url = getUrlToReloadTo(accessToken);

        hardReload(url);
    } catch (error) {
        logger.error('Self token exchange failed: ', error);
    }
}

export function getHandoverLanguage() {
    const currentLanguage = getCurrentLanguage();

    if (currentLanguage === getFeatureConfig((config) => config.base.defaultLanguage)) {
        return currentLanguage;
    }

    return EN_LANGUAGE;
}

export function getReturnState(returnRoute: string) {
    const returnState = `handover/success/${returnRoute}`;

    if (isCsrfFeatureEnabled()) {
        const csrfToken = uuid();
        setCookie(CSRF_TOKEN_COOKIE, csrfToken);
        return `${returnState}||${csrfToken}`;
    }

    return returnState;
}

export function getHandoverState(target: string | undefined | null, returnRoute: string) {
    let redirectUri = `${document.location.protocol}//${document.location.hostname}`;
    if (getEnv<RetailEnvironmentConfig>().environment === 'localDev') {
        redirectUri += `:8080?country=${getEnv<RetailEnvironmentConfig>().country}`;
    }
    let state: Record<string, string> = {
        srcClientId: getEnv<RetailEnvironmentConfig>().clientId,
        redirectUri,
        returnState: getReturnState(returnRoute),
    };

    if (target) {
        state = { ...state, target };
    }

    return base64UrlEncode(JSON.stringify(state));
}
